// Shared HTTP + session client. Moved out of main.tsx so the pages that live
// in their own files can talk to the API without importing the 9k-line entry
// module (which would make the import graph circular).

export const API_BASE = import.meta.env.VITE_NAVOPTION_API_BASE || 'https://api.navoption.com'
const AUTH_KEY = 'navoption.auth.session'

export type ApiError = Error & { status?: number }

export function isSignedIn() {
  try {
    return JSON.parse(localStorage.getItem(AUTH_KEY) || 'null')?.signed_in === true
  } catch {
    return false
  }
}

export function setSignedIn(value: boolean) {
  if (value) {
    localStorage.setItem(AUTH_KEY, JSON.stringify({ signed_in: true }))
  } else {
    localStorage.removeItem(AUTH_KEY)
  }
}

export async function silentRefresh(): Promise<boolean> {
  try {
    const r = await fetch(`${API_BASE}/v1/auth/refresh`, { method: 'POST', credentials: 'include' })
    return r.ok
  } catch {
    return false
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const doFetch = () => fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers || {}),
    },
  })
  let res = await doFetch()
  // The web access cookie is 1h; refresh cookie is 90d. On 401 try a one-shot
  // silent rotation through /v1/auth/refresh then retry the original call.
  // iOS does the same in NavOptionAPIClient. Avoid recursing on refresh itself.
  if (res.status === 401 && !path.startsWith('/v1/auth/refresh') && !path.startsWith('/v1/auth/google') && !path.startsWith('/v1/auth/apple')) {
    if (await silentRefresh()) {
      res = await doFetch()
    }
  }
  const json = await res.json().catch(() => ({}))
  if (!res.ok || json.ok === false) {
    const err = new Error(json?.error?.message || json?.message || 'Request failed') as ApiError
    err.status = res.status
    throw err
  }
  if (Array.isArray(json.data) && Array.isArray(json.headers)) {
    return json as T
  }
  return (json.data ?? json) as T
}

export function navigate(path: string) {
  window.history.pushState({}, '', path)
  window.dispatchEvent(new PopStateEvent('popstate'))
}
